import { Button, TextField, Paper } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import { RefreshActions } from '../state/refresh'
import {
  isAnyStringEmpty,
  validateUsername,
  validatePassword
} from '../utils/validators'
import { useRequests } from '../requests/useRequests'
import Avatar from '@mui/material/Avatar'
import Link from '@mui/material/Link'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import { backgroundStyle } from '../styles/background'
import { GoogleLogin } from '@react-oauth/google'

const Login = (): JSX.Element => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const requestMaker = useRequests()
  const [username, setUsername] = useState<string>('testuser123')
  const [password, setPassword] = useState<string>('password')
  const [usernameError, setUsernameError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [isActiveButton, setIsActiveButton] = useState(false)
  const [isRegisterRedirect, setIsRegisterRedirect] = useState(false)
  const [isLoginSuccesfull, setIsLoginSuccesfull] = useState(false)

  useEffect(() => {
    if (!isAnyStringEmpty([username, password])) {
      setIsActiveButton(true)
    } else {
      setIsActiveButton(false)
    }
  }, [username, password])

  const handleLogIn = async (): Promise<void> => {
    if (
      validateUsername(username, setUsernameError) &&
      validatePassword(password, setPasswordError)
    ) {
      try {
        const tokenResponse = await requestMaker.getToken({
          username,
          password
        })
        localStorage.setItem('vrema-username', username)
        localStorage.setItem('vrema-password', password)
        localStorage.setItem('vrema-jwtToken', tokenResponse.data.Authorization)
        dispatch(
          RefreshActions.setToastMessage({ message: 'Login succesfull' })
        )
        dispatch(RefreshActions.refreshUserState())
        setIsLoginSuccesfull(true)
      } catch (e) {
        setPasswordError('Incorrect password')
      }
    }
  }

  const handleGoogleLogIn = async (credentials: {
    credential: string
    select_by: string
    clientId: string
  }): Promise<void> => {
    try {
      const googleToken = credentials.credential
      const tokenResponse = await requestMaker.getToken({
        googleToken
      })
      localStorage.setItem('vrema-googleToken', googleToken)
      localStorage.setItem('vrema-jwtToken', tokenResponse.data.Authorization)
      dispatch(RefreshActions.setToastMessage({ message: 'Login succesfull' }))
      dispatch(RefreshActions.refreshUserState())
      setIsLoginSuccesfull(true)
    } catch (e) {
      setPasswordError('Incorrect login')
    }
  }

  const handleChangeUsername = async (username: string): Promise<void> => {
    setUsername(username)
    if (username === '') {
      return
    }
    const response = await requestMaker.emailOrUsernameExists(username)

    if (response.data) {
      setUsernameError(null)
    } else {
      setUsernameError('User with this username does not exist')
    }
  }

  useEffect(() => {
    if (isRegisterRedirect) {
      navigate('/register')
    }
    if (isLoginSuccesfull) {
      navigate('/statistics')
    }
  }, [isRegisterRedirect, isLoginSuccesfull, navigate])

  return (
    <form
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault()
          void handleLogIn()
        }
      }}
    >
      <Container component='main' maxWidth='xs'>
        <div style={backgroundStyle} />
        <Paper
          elevation={0}
          sx={{
            marginTop: 8,
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '24px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Avatar
              style={{
                margin: '8px',
                width: 56,
                height: 56,
                backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 10px 25px rgba(102, 126, 234, 0.4)'
              }}
            >
              <LockOutlinedIcon />
            </Avatar>
            <Typography
              component='h1'
              variant='h5'
              style={{
                marginBottom: '32px',
                fontWeight: 600,
                color: '#2d3748',
                fontSize: '1.75rem'
              }}
            >
              Sign in
            </Typography>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <GoogleLogin
                onSuccess={handleGoogleLogIn as any}
                onError={() => {
                  setPasswordError('There was an error during google login')
                }}
                auto_select
              />
            </Box>
            <TextField
              error={usernameError !== null}
              variant='outlined'
              helperText={usernameError}
              value={username}
              onChange={e => {
                setUsernameError(null)
                setUsername(e.target.value)
              }}
              onBlur={e => {
                void handleChangeUsername(e.target.value)
              }}
              margin='normal'
              required
              fullWidth
              id='username'
              label='Username or Email'
              name='username'
              autoComplete='username'
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&:hover fieldset': {
                    borderColor: '#667eea'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea'
                  }
                }
              }}
            />
            <TextField
              error={passwordError !== null}
              variant='outlined'
              helperText={passwordError}
              type='password'
              value={password}
              onChange={e => {
                setPasswordError(null)
                setPassword(e.target.value)
              }}
              margin='normal'
              required
              fullWidth
              name='password'
              label='Password'
              id='password'
              autoComplete='current-password'
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&:hover fieldset': {
                    borderColor: '#667eea'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea'
                  }
                }
              }}
            />
            <FormControlLabel
              control={<Checkbox value='remember' sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />}
              label='Remember me'
              sx={{ alignSelf: 'flex-start', mt: 1 }}
            />
            <Button
              type='submit'
              fullWidth
              variant='contained'
              style={{
                marginTop: '24px',
                marginBottom: '16px',
                paddingTop: '12px',
                paddingBottom: '12px',
                borderRadius: '12px',
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 600,
                backgroundImage: !isActiveButton ? 'linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
              }}
              sx={{
                '&:hover': {
                  backgroundImage: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)'
                }
              }}
              disabled={!isActiveButton}
              onClick={e => {
                e.preventDefault()
                void handleLogIn()
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void handleLogIn()
                }
              }}
              tabIndex={0}
            >
              Sign In
            </Button>
            <Grid container>
              <Grid item xs>
                <Link
                  style={{
                    cursor: 'pointer',
                    color: '#667eea',
                    fontWeight: 500,
                    textDecoration: 'none'
                  }}
                  variant='body2'
                  onClick={() => navigate('/forgot-password')}
                  sx={{
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Forgot password?
                </Link>
              </Grid>
              <Grid item>
                <Link
                  style={{
                    cursor: 'pointer',
                    color: '#667eea',
                    fontWeight: 500,
                    textDecoration: 'none'
                  }}
                  variant='body2'
                  onClick={() => setIsRegisterRedirect(true)}
                  sx={{
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Don't have an account? Sign Up
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </form>
  )
}

export default Login
